import React, { useRef, useEffect, useCallback, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './SafeIcon';

const { FiBold, FiItalic, FiLink, FiLink2 } = FiIcons;

const ToolbarBtn = ({ onClick, active, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold transition-all
      ${active
        ? 'bg-primary text-white shadow-sm'
        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
      }`}
  >
    {children}
  </button>
);

const LinkModal = ({ onConfirm, onCancel, initialUrl = '' }) => {
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); onConfirm(url, text); }
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="absolute z-50 top-full left-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 p-5">
      <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Insert Hyperlink</h4>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1">Link Text (optional)</label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Display text (uses selection if blank)"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1">URL *</label>
          <input
            ref={inputRef}
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="https://example.com"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-400 hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm(url, text)}
          disabled={!url.trim()}
          className="flex-1 py-2 rounded-xl text-xs font-bold bg-primary text-white hover:bg-blue-800 transition-colors disabled:opacity-40"
        >
          Insert Link
        </button>
      </div>
    </div>
  );
};

/**
 * Lightweight rich-text editor using contentEditable.
 * Stores output as HTML string — compatible with the existing
 * back-matter JSON blob and EPUB builder (nl2br / htmlspecialchars).
 *
 * Props:
 *   value       {string}   — HTML string (controlled)
 *   onChange    {fn}       — called with new HTML string
 *   placeholder {string}
 *   minHeight   {string}   — Tailwind min-h class, default 'min-h-[120px]'
 */
const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  minHeight = 'min-h-[120px]',
}) => {
  const editorRef = useRef(null);
  const isInternalUpdate = useRef(false);
  const savedRange = useRef(null);
  const [activeFormats, setActiveFormats] = useState({ bold: false, italic: false, link: false });
  const [showLinkModal, setShowLinkModal] = useState(false);

  // ── Sync external value → DOM (only when value changes externally) ──────────
  useEffect(() => {
    const el = editorRef.current;
    if (!el || isInternalUpdate.current) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || '';
    }
  }, [value]);

  // ── Save selection before modal opens (modal steals focus) ──────────────────
  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  }, []);

  // ── Update active format state on selection change ─────────────────────────
  const updateActiveFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      link: !!getSelectedAnchor(),
    });
  }, []);

  const getSelectedAnchor = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    let node = sel.getRangeAt(0).commonAncestorContainer;
    while (node && node !== editorRef.current) {
      if (node.nodeName === 'A') return node;
      node = node.parentNode;
    }
    return null;
  };

  // ── Emit change ─────────────────────────────────────────────────────────────
  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    isInternalUpdate.current = true;
    onChange(el.innerHTML);
    requestAnimationFrame(() => { isInternalUpdate.current = false; });
  }, [onChange]);

  // ── Format commands ─────────────────────────────────────────────────────────
  const execBold = () => { document.execCommand('bold'); editorRef.current?.focus(); emitChange(); updateActiveFormats(); };
  const execItalic = () => { document.execCommand('italic'); editorRef.current?.focus(); emitChange(); updateActiveFormats(); };

  const openLinkModal = () => {
    saveSelection();
    const anchor = getSelectedAnchor();
    setShowLinkModal(anchor ? anchor.href : true);
  };

  const confirmLink = (url, linkText) => {
    setShowLinkModal(false);
    restoreSelection();
    editorRef.current?.focus();
    if (!url.trim()) return;

    const sel = window.getSelection();
    const hasSelection = sel && sel.toString().trim().length > 0;

    if (hasSelection) {
      // Wrap selected text in <a>
      document.execCommand('createLink', false, url);
      // Set target blank on the newly created link
      const anchor = getSelectedAnchor();
      if (anchor) { anchor.target = '_blank'; anchor.rel = 'noopener noreferrer'; }
    } else {
      // Insert new link with custom text
      const displayText = linkText.trim() || url;
      const a = `<a href="${url}" target="_blank" rel="noopener noreferrer">${displayText}</a>`;
      document.execCommand('insertHTML', false, a);
    }
    emitChange();
    updateActiveFormats();
  };

  const removeLink = () => {
    const anchor = getSelectedAnchor();
    if (anchor) {
      // Unwrap the <a> tag
      const parent = anchor.parentNode;
      while (anchor.firstChild) parent.insertBefore(anchor.firstChild, anchor);
      parent.removeChild(anchor);
      emitChange();
      updateActiveFormats();
    }
  };

  const handleKeyDown = (e) => {
    // Ctrl/Cmd+B → bold, Ctrl/Cmd+I → italic
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'b') { e.preventDefault(); execBold(); }
      if (e.key === 'i') { e.preventDefault(); execItalic(); }
    }
    // Close link modal on Escape
    if (e.key === 'Escape' && showLinkModal) setShowLinkModal(false);
  };

  const isLinkActive = !!getSelectedAnchor();

  return (
    <div className="relative border border-slate-200 rounded-xl overflow-visible focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all">
      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-xl">
        <ToolbarBtn onClick={execBold} active={activeFormats.bold} title="Bold (Ctrl+B)">
          <SafeIcon icon={FiBold} />
        </ToolbarBtn>
        <ToolbarBtn onClick={execItalic} active={activeFormats.italic} title="Italic (Ctrl+I)">
          <SafeIcon icon={FiItalic} />
        </ToolbarBtn>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarBtn
          onClick={isLinkActive ? removeLink : openLinkModal}
          active={isLinkActive}
          title={isLinkActive ? 'Remove link' : 'Insert hyperlink'}
        >
          <SafeIcon icon={isLinkActive ? FiLink2 : FiLink} />
        </ToolbarBtn>
        <span className="ml-auto text-[10px] text-slate-300 font-bold uppercase tracking-widest hidden sm:block">
          Ctrl+B · Ctrl+I
        </span>
      </div>

      {/* ── Editable area ───────────────────────────────────────────────────── */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onKeyDown={handleKeyDown}
        onMouseUp={updateActiveFormats}
        onKeyUp={updateActiveFormats}
        onFocus={updateActiveFormats}
        data-placeholder={placeholder}
        className={`px-4 py-3 text-sm text-slate-700 outline-none ${minHeight} resize-y overflow-auto
          [&_a]:text-primary [&_a]:underline [&_a]:cursor-pointer
          [&_strong]:font-bold [&_b]:font-bold
          [&_em]:italic [&_i]:italic
          empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none`}
        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      />

      {/* ── Link Modal ──────────────────────────────────────────────────────── */}
      {showLinkModal && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowLinkModal(false)}
          />
          <LinkModal
            initialUrl={typeof showLinkModal === 'string' ? showLinkModal : ''}
            onConfirm={confirmLink}
            onCancel={() => setShowLinkModal(false)}
          />
        </>
      )}
    </div>
  );
};

export default RichTextEditor;